// "Edit itinerary" modal of a ShareJet booking: departure date/time, origin and destination.
import { useEffect } from 'react';
import { App as AntdApp, DatePicker, Form, Modal, Space, TimePicker } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import { AirportSelect } from '../../components/AirportSelect';
import { SameCityNotice } from '../../components/SameCityNotice';
import type { AdminBookingDetail } from '../../api/types';
import { extractError, type SubmitFn } from './bookingUpdate';
import { useSingleSubmit } from '../../hooks/useSingleSubmit';

// The form parses the booking's 'YYYY-MM-DD' / 'HH:mm' strings with a format.
dayjs.extend(customParseFormat);

export function ItineraryModal({
  open,
  onClose,
  booking,
  onSubmit,
  submitting,
}: {
  open: boolean;
  onClose: () => void;
  booking: AdminBookingDetail;
  onSubmit: SubmitFn;
  submitting: boolean;
}) {
  const { message } = AntdApp.useApp();
  const [form] = Form.useForm<{
    departureDate: Dayjs;
    departureTime: Dayjs;
    originCode: string;
    destCode: string;
  }>();
  useEffect(() => {
    if (open) {
      form.setFieldsValue({
        departureDate: dayjs(booking.departureDate, 'YYYY-MM-DD'),
        departureTime: dayjs(booking.departureTime, 'HH:mm'),
        originCode: booking.route.origin.code,
        destCode: booking.route.dest.code,
      });
    }
  }, [open, booking, form]);

  // Regression 28 Sep — one submit at a time.
  const submitGuard = useSingleSubmit();
  const handleOk = submitGuard.wrap(async () => {
    const v = await form.validateFields();
    try {
      await onSubmit({
        departureDate: v.departureDate.format('YYYY-MM-DD'),
        departureTime: v.departureTime.format('HH:mm'),
        originCode: v.originCode?.trim().toUpperCase(),
        destCode: v.destCode?.trim().toUpperCase(),
      });
      message.success('Itinerary updated');
      onClose();
    } catch (e) {
      // Surfaces the backend's same-city constraint (400) message.
      message.error(extractError(e));
    }
  });

  return (
    <Modal
      title="Edit itinerary"
      open={open}
      onOk={handleOk}
      onCancel={onClose}
      confirmLoading={submitting || submitGuard.busy}
      destroyOnClose
    >
      {/* SHAR-758 (Copilot review) — shared with the charter itinerary editor,
          which enforces the same rule. */}
      <SameCityNotice />
      <Form form={form} layout="vertical">
        <Space size="middle" style={{ display: 'flex' }}>
          <Form.Item
            name="departureDate"
            label="Departure date"
            rules={[{ required: true, message: 'Required' }]}
            style={{ flex: 1 }}
          >
            <DatePicker format="YYYY-MM-DD" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="departureTime"
            label="Departure time"
            rules={[{ required: true, message: 'Required' }]}
            style={{ flex: 1 }}
          >
            <TimePicker format="HH:mm" minuteStep={5} style={{ width: '100%' }} />
          </Form.Item>
        </Space>
        <Space size="middle" style={{ display: 'flex', alignItems: 'flex-start' }}>
          <Form.Item
            name="originCode"
            label="Origin airport"
            rules={[{ required: true, message: 'Required' }]}
            style={{ flex: 1 }}
          >
            <AirportSelect />
          </Form.Item>
          <Form.Item
            name="destCode"
            label="Destination airport"
            rules={[{ required: true, message: 'Required' }]}
            style={{ flex: 1 }}
          >
            <AirportSelect />
          </Form.Item>
        </Space>
      </Form>
    </Modal>
  );
}
